import ReactApexChart from 'react-apexcharts'
import { useParams } from 'react-router-dom'
import { useStatsSingleProject } from '../../../hooks/useStatsSingleProject'
import { Alert } from 'antd'

type Category = {
  title: string
  /** Success, Failed, Awaiting */
  tooltipPostfix?: { Success?: string; Failed?: string; Awaiting?: string }
}

/** tooltipPostFixes are index of  */
const categories: Category[] = [
  {
    title: 'Facebook',
    tooltipPostfix: { Success: 'people clicked an ad', Failed: 'people reached without clicking an ad', Awaiting: '' },
  },
  {
    title: 'Landingpage',
    tooltipPostfix: { Success: 'people signed up.', Failed: 'clicked ad without signing up.', Awaiting: '' },
  },
  {
    title: 'Prescreening',
    tooltipPostfix: {
      Success: 'finished prescreening eligible',
      Failed: 'finished prescreening ineligible',
      Awaiting: "didn't finish prescreening yet",
    },
  },
  {
    title: 'Phonescreening',
    tooltipPostfix: {
      Success: 'phonescreened eligible',
      Failed: 'phonescreened ineligible',
      Awaiting: "didn't complete phonescreening yet",
    },
  },
  {
    title: 'Qualified Leads',
    tooltipPostfix: {
      Success: 'qualified leads received a screening appointment',
      Failed: 'qualified leads were cancelled',
      Awaiting: 'qualified leads are ready to schedule a screening appointment.',
    },
  },
  {
    title: 'Scheduled screening',
    tooltipPostfix: {
      Success: 'with a screening appointment signed IC',
      Failed: 'were no-shows or non-qualified',
      Awaiting: 'waiting for the appointment',
    },
  },
  {
    title: 'Screening',
    tooltipPostfix: {
      Success: 'received a randomization appointment',
      Failed: 'screen failures',
      Awaiting: 'are waiting to schedule a randomization appointment.',
    },
  },
  {
    title: 'Scheduled Randomization',
    tooltipPostfix: {
      Success: 'were randomized',
      Failed: 'failed randomization',
      Awaiting: 'are waiting for the randomization appointment.',
    },
  },
  /* { title: 'Randomization', tooltipPostfix: { Success: '', Failed: '', Awaiting: '' } }, */
]

// must match the above keys.
const seriesNames = ['Success', 'Failed', 'Awaiting'] as const

export const FunnelBarChart = () => {
  const { projectId } = useParams() as { projectId: string }
  const { barChartData, numSignUps, facebookCampaignsInsights } = useStatsSingleProject(projectId)

  const adViewsUnique = facebookCampaignsInsights.reduce((acc, campaign) => {
    return acc + Number(campaign.reach)
  }, 0)

  const adClicksUnique = facebookCampaignsInsights.reduce((acc, campaign) => {
    return acc + Number(campaign.unique_clicks)
  }, 0)

  const state = {
    series: [
      {
        color: '#66DA26',
        name: seriesNames[0], // success
        data: [adClicksUnique, numSignUps, ...barChartData.success],
      },
      {
        color: '#E91E63',
        name: seriesNames[1], // failed
        data: [adViewsUnique - adClicksUnique, adClicksUnique - numSignUps, ...barChartData.failed],
      },
      {
        color: '#c7b9ab',
        name: seriesNames[2], // awaiting
        data: [0, 0, ...barChartData.unknown],
      },
    ],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        stackType: '100%',
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          dataLabels: {
            enabled: true,
          },
        },
      },
      stroke: {
        width: 1,
        colors: ['#fff'],
      },
      title: {
        text: 'Performance',
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return Math.round(Number(val)) + '%'
        },
      },
      xaxis: {
        categories: categories.map((c) => c.title),
        labels: {
          formatter: function (val) {
            return val + ''
          },
        },
      },
      yaxis: {
        title: {
          text: undefined,
        },
        //logarithmic: true,
        min: 1,
      },
      tooltip: {
        y: {
          formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
            const categoryTitle = w.globals.labels[dataPointIndex]
            const categoryObj = categories.find((c) => c.title === categoryTitle)

            if (!categoryObj?.tooltipPostfix) return value

            const serieName: 'Success' | 'Failed' | 'Awaiting' = seriesNames[seriesIndex]
            const postfix = categoryObj.tooltipPostfix[serieName] ?? ''

            return `${value} ${postfix}`
          },
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        offsetX: 40,
      },
    },
  }

  return (
    <div style={{ height: '100%' }}>
      <div className="chart" style={{ height: '100%' }}>
        {/*@ts-ignore */}
        <ReactApexChart options={state.options} series={state.series} type="bar" height={350} />
        {facebookCampaignsInsights.length === 0 && (
          <Alert
            style={{ marginBottom: 20 }}
            type="warning"
            message="No Facebook campaigns selected in Admin Module!"
          />
        )}
      </div>
      <div id="html-dist-barchart"></div>
    </div>
  )
}
